import { ArrowLeft, ChevronLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { LogoMark } from '@/components/Wordmark'
import type { Me } from '@/lib/api'
import { barOnScroll, type BarScroll } from '@/lib/barOnScroll'
import { useFrame } from '@/lib/frame'
import { cn } from '@/lib/utils'
import { WaitingDot } from './WaitingDot'
import type { Place } from './places'
import { ProfileMenu } from './ProfileMenu'
import { YouFace } from './YouFace'

// notesfrom's phone shell: a slim bar on top and the places as tabs at the bottom. Nothing here shows on a tablet or
// computer. The bar itself is clear, so the page shows through it: only its buttons stand out, on frosted rounds like
// the tabs'. Only Home has the GF mark, at the left as Instagram's is, balancing Home's buttons at the right (Dallas,
// 2026-10-18); everywhere else the page's own top row moves up into the middle (BarRow), so the page starts right under
// the clock (Dallas, 2026-10-17). A page you've
// stepped into has back at the left (iOS's chevron, Android's arrow); a tab's own page has nothing there, its name
// being left to screen readers. The right end holds the page's "+" (CreateAction) and Select (SelectToggle). You sit at the end of the tabs, as on
// Instagram; only a page without tabs keeps you at the top right. The bar tucks away as you scroll down the page and
// comes back as you scroll up (lib/barOnScroll.ts).
export function PhoneHeader({ me, profile, logo }: { me: Me; profile: boolean; logo: boolean }) {
  const { page, setActionSlot, setRowSlot } = useFrame()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { hidden, scrolled } = useBarOnScroll(pathname)
  return (
    <>
      {/* Behind the clock and battery the page shows through, under a soft fade so the time stays readable over photos
          (in the home-screen app; elsewhere it's 0 tall). Never a solid band (Dallas, 2026-10-18). iOS keeps the clock
          style the app was added with, and a see-through one always draws the clock white, so in light the fade is
          ink rather than paper. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 z-30 h-(--safe-top) bg-linear-to-b from-foreground/80 via-foreground/55 to-transparent nav:hidden dark:from-background/70 dark:via-background/35"
      />
      {/* Clear even with the page under it (Dallas, 2026-10-18); data-scrolled lets tabs in it take a faint pill then. */}
      <header
        data-hidden={hidden || undefined}
        data-scrolled={scrolled || undefined}
        className={cn(
          'sticky top-(--vv-top) z-20 grid min-h-[calc(var(--bar-h)+var(--safe-top))] items-center pt-(--safe-top) pr-[max(0.75rem,var(--safe-right))] pl-[max(0.75rem,var(--safe-left))] transition-transform duration-300 ease-page focus-within:translate-y-0 motion-reduce:transition-none nav:hidden android:min-h-[calc(3.5rem+var(--safe-top))]',
          'grid-cols-[auto_minmax(0,1fr)_auto]',
          // Past wherever it's pinned (lib/viewport.ts), so none of it is left on screen.
          hidden && '-translate-y-[calc(100%+var(--vv-top))]',
        )}
      >
        <div className="flex min-w-0 items-center [&:not(:empty)]:pr-2">
          {page.back && (
            // Back goes back, to wherever you came from in the app; opened straight from a link, it goes up a level.
            <Link
              to={page.back.to}
              aria-label={page.back.label}
              title={page.back.label}
              onClick={(e) => {
                if ((window.history.state?.idx ?? 0) === 0) return
                e.preventDefault()
                navigate(-1)
              }}
              className={cn(glass, 'grid size-11 place-items-center active:opacity-60 android:size-12')}
            >
              <ChevronLeft className="size-6.5 stroke-[2] android:hidden" aria-hidden />
              <ArrowLeft className="hidden size-6 android:block" aria-hidden />
            </Link>
          )}
          {logo && !page.back && <LogoMark label className="h-7" />}
        </div>
        <div className="flex min-w-0 items-center justify-center">
          {/* Always here on a phone, so a page's row moves up as it first draws rather than once it's on screen. */}
          <div ref={setRowSlot} data-slot="bar-row" className="flex min-w-0 flex-1 items-center empty:hidden" />
        </div>
        <div className="flex items-center justify-end gap-2 pl-2">
          <div ref={setActionSlot} data-slot="bar-actions" className={cn(glass, 'flex items-center empty:hidden')} />
          {profile && (
            <ProfileMenu me={me}>
              <button
                type="button"
                aria-label={`${me.name}, profile`}
                className="grid size-11 place-items-center rounded-xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <YouFace me={me} className="size-7 text-xs" />
              </button>
            </ProfileMenu>
          )}
        </div>
      </header>
    </>
  )
}

// The bar's buttons on a frosted round, so they read over whatever the page has under them.
const glass =
  'rounded-full bg-background/75 shadow-[0_1px_8px_rgb(0_0_0/0.08)] ring-1 ring-border/70 outline-none backdrop-blur-xl backdrop-saturate-150 focus-visible:ring-[3px] focus-visible:ring-ring/50'

// Whether the bar is tucked away. It's always back on a new page, and while something is being picked (Done is in the
// bar, data-keep-bar).
function useBarOnScroll(pathname: string) {
  // Tucked away on which page: on any other, it shows. The same for the page having scrolled under the bar.
  const [hiddenOn, setHiddenOn] = useState<string | null>(null)
  const [scrolledOn, setScrolledOn] = useState<string | null>(null)
  useEffect(() => {
    let was: BarScroll = { hidden: false, anchor: window.scrollY }
    const onScroll = () => {
      const root = document.documentElement
      const keep = document.querySelector('[data-keep-bar]') !== null
      const next = barOnScroll(was, window.scrollY, root.scrollHeight - window.innerHeight, 56)
      was = keep ? { hidden: false, anchor: next.anchor } : next
      setHiddenOn(was.hidden ? pathname : null)
      setScrolledOn(window.scrollY > 0 ? pathname : null)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [pathname])
  return { hidden: hiddenOn === pathname, scrolled: scrolledOn === pathname }
}

const tab =
  'grid h-full place-items-center rounded-full text-muted-foreground outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-[current=page]:text-brand [&:active>*]:scale-90 [&>*]:transition-transform'

// A tab's name under its icon: small, and never wider than its tab.
const tabLabel = 'max-w-full truncate text-[0.6875rem] leading-none font-medium'

// The most places that get a tab of their own. Past that, the places that don't fit (Recipes and Calendar, with
// every section on) are in your profile's sheet, with the circles, so the tabs stay a short row under a thumb.
export const TABS = 5

// The tabs float a little above the bottom of the screen as a frosted pill, the way Instagram's and Facebook's do,
// each with its short name under its icon so a first visit needs no guessing (Dallas, 2026-10-18). It rides the
// visible part of the screen through --vv-below, like everything pinned to the bottom (lib/viewport.ts).
export function TabBar({ me, places, hidden }: { me: Me; places: Place[]; hidden?: boolean }) {
  const { pathname } = useLocation()
  const yours = /^\/(me|preferences|invites|circle\/new)(\/|$)/.test(pathname)
  const tabs = places.length > TABS ? places.slice(0, TABS - 1) : places
  const more = places.slice(tabs.length)
  const inMore = more.some((place) => place.active)
  return (
    <nav
      aria-label="Places"
      className={cn(
        'fixed inset-x-4 bottom-[calc(var(--tab-float)-var(--vv-below))] z-20 mx-auto grid h-(--pill-h) max-w-md auto-cols-fr grid-flow-col rounded-full border border-border/70 bg-background/80 p-1 shadow-float backdrop-blur-xl backdrop-saturate-150 transition-transform duration-300 ease-page nav:hidden',
        hidden && 'translate-y-[calc(100%+var(--tab-float)+1rem)]',
      )}
    >
      {tabs.map((place) => (
        <Link
          key={place.key}
          to={place.to}
          aria-label={place.label}
          title={place.label}
          aria-current={place.active ? 'page' : undefined}
          className={tab}
        >
          <span className="flex flex-col items-center gap-0.5">
            <span className="relative">
              <place.icon className="size-6" active={place.active} />
              {place.dot && <WaitingDot className="absolute -top-0.5 -right-0.5" />}
            </span>
            <span aria-hidden className={tabLabel}>
              {place.label}
            </span>
          </span>
        </Link>
      ))}
      <ProfileMenu me={me} side="top" align="end" places={more}>
        <button
          type="button"
          aria-label={`${me.name}, profile`}
          title="You"
          aria-current={inMore ? 'page' : undefined}
          className={tab}
        >
          <span className="flex flex-col items-center gap-0.5">
            {/* On your own pages (profile, invites, a new circle) it's marked as the tab you're on instead. */}
            <YouFace
              me={me}
              plain={yours}
              className={cn(
                'size-6 text-[0.625rem]',
                yours && 'ring-[1.5px] ring-foreground ring-offset-2 ring-offset-background',
              )}
            />
            <span aria-hidden className={tabLabel}>
              You
            </span>
          </span>
        </button>
      </ProfileMenu>
    </nav>
  )
}
